import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import supabase from '../../services/supabase';
import "./ProfileSetup.css";

export default function ProfileSetup() {
  const [username, setUsername] = useState("");
  const [name, setName] = useState("");
  const [bio, setBio] = useState("");
  const [location, setLocation] = useState("");
  const [gender, setGender] = useState(""); // New gender state
  const [imageFile, setImageFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUser = async () => {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error) {
        console.error("Error fetching user:", error);
        return;
      }

      setUser(user);

      if (user) {
        const { data, error } = await supabase
          .from("user_profiles")
          .select("*")
          .eq("user_id", user.id)
          .maybeSingle();

        if (data && !error) {
          setUsername(data.username || "");
          setName(data.name || "");
          setBio(data.bio || "");
          setLocation(data.homeLocation || "");
          setGender(data.gender || ""); // Prefill gender
        }
      }
    };

    fetchUser();
  }, []);

  const handleSubmit = async () => {
    if (!user) {
      alert("You must be logged in to set up your profile");
      return;
    }

    setLoading(true);

    try {
      let imageUrl = "";
      if (imageFile) {
        const fileExt = imageFile.name.split(".").pop();
        const filePath = `${user.id}/${Date.now()}.${fileExt}`;

        const { error: uploadError } = await supabase.storage
          .from("profile-pictures")
          .upload(filePath, imageFile, {
            upsert: true,
          });

        if (uploadError) {
          alert("Failed to upload image: " + uploadError.message);
          console.error(uploadError);
          setLoading(false);
          return;
        }

        const { data: publicUrl } = supabase.storage
          .from("profile-pictures")
          .getPublicUrl(filePath);

        imageUrl = publicUrl.publicUrl;
      }

      const { data: existingProfile, error: fetchError } = await supabase
        .from("user_profiles")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();

      if (fetchError && fetchError.code !== "PGRST116") {
        alert("Error checking for existing profile");
        console.error(fetchError);
        setLoading(false);
        return;
      }

      let error;

      if (existingProfile) {
        const { error: updateError } = await supabase
          .from("user_profiles")
          .update({
            username,
            name,
            bio,
            homeLocation: location,
            gender, // Include gender on update
            profilePicture: imageUrl || existingProfile.profilePicture,
          })
          .eq("user_id", user.id);

        error = updateError;
      } else {
        const { error: insertError } = await supabase
          .from("user_profiles")
          .insert({
            user_id: user.id,
            username,
            name,
            bio,
            homeLocation: location,
            gender, // Include gender on insert
            profilePicture: imageUrl,
            score: 0,
            post_count: 0,
            destination_count: 0,
            follower_count: 0,
            sharing_enabled: false,
            notifications_enabled: false,
          });

        error = insertError;
      }

      if (error) {
        alert("Error saving profile: " + error.message);
        console.error(error);
      } else {
        navigate("/localmap");
      }
    } catch (error) {
      console.error("Unexpected error:", error);
      alert("An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="profile-setup-container">
      <h2>Finish Setting Up Your Profile</h2>

      <input
        type="text"
        placeholder="Username"
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        required
      />

      <input
        type="text"
        placeholder="Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />

      <input
        type="text"
        placeholder="Location"
        value={location}
        onChange={(e) => setLocation(e.target.value)}
      />

      <select
        value={gender}
        onChange={(e) => setGender(e.target.value)}
        className="gender-select"
      >
        <option value="">Select Gender</option>
        <option value="male">Male</option>
        <option value="female">Female</option>
        <option value="non-binary">Non-binary</option>
        <option value="prefer-not-to-say">Prefer not to say</option>
      </select>

      <textarea
        placeholder="Bio"
        value={bio}
        onChange={(e) => setBio(e.target.value)}
      />

      <label className="upload-label">
        Upload Profile Picture
        <input
          type="file"
          accept="image/*"
          onChange={(e) => setImageFile(e.target.files[0])}
        />
      </label>

      {imageFile && (
        <div className="preview">
          <p>Selected file: {imageFile.name}</p>
        </div>
      )}

      <button
        className="save-btn"
        onClick={handleSubmit}
        disabled={loading}
      >
        {loading ? "Saving..." : "Save Profile"}
      </button>
    </div>
  );
}
